import api from "@/api/v1-jwt/config/api";
import { Button } from "@/components/ui/button-tw";
import { useQuery } from "@tanstack/react-query";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Mail,
  Minus,
  Plus,
  ZoomIn,
} from "lucide-react";
import React, { useEffect, useState, useRef, useCallback } from "react";

export default function PayInvoicePreviewPage() {
  const { data, htmlContent, logoUrl, isLoading } = useInvoicePreview();
  const {
    pages,
    canvasRef,
    pageNumber,
    numPages,
    zoomIn,
    zoomOut,
    scale,
    handleTouchStart,
    handleTouchEnd,
    goToNextPage,
    goToPrevPage,
  } = useCanvasPreview(htmlContent);

  const handlePayInvoice = () => {
    if (data?.location) {
      window.location.href = data?.location;
    }
  };

  const downloadInvoicePdf = async () => {
    if (!pages.length) {
      alert("No content available to download.");
      return;
    }

    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidthMm = 210;
      const pdfHeightMm = 297;

      pages.forEach((pageCanvas, index) => {
        const imgWidth = pageCanvas.width;
        const imgHeight = pageCanvas.height;
        const ratio = Math.min(pdfWidthMm / imgWidth, pdfHeightMm / imgHeight);
        const scaledWidth = imgWidth * ratio;
        const scaledHeight = imgHeight * ratio;

        const imgData = pageCanvas.toDataURL("image/png", 1.0);
        if (index > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, scaledWidth, scaledHeight);
      });

      const invoiceNumber = data?.invoice_number;
      pdf.save(`Invoice_${invoiceNumber ?? new Date().toISOString().split("T")[0]}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <div className="min-h-full bg-gray-50">
      <nav className="bg-skin-primary shadow-xs">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex shrink-0 items-center">
              <img
                alt="Company logo"
                src={logoUrl}
                className="block w-auto h-10 md:h-16"
              />
            </div>
            <div className="sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
              <div className="flex gap-3">
                <Button
                  size="icon"
                  variant="outline"
                  className="lg:hidden"
                  onClick={downloadInvoicePdf}
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={downloadInvoicePdf}
                  className="hidden lg:flex"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Invoice
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePayInvoice}
                  disabled={!data?.location}
                >
                  <span className="lg:hidden">Pay</span>
                  <span className="hidden lg:inline ">Pay Invoice</span>{" "}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="pb-6 pt-3">
        <main>
          <div className="mx-auto max-w-2xl px-4  sm:px-6 lg:px-8">
            <div className="rounded-lg overflow-hidden shadow-xl">
              {isLoading ? (
                <div className="flex items-center justify-center p-10">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : !data?.data?.base64_html ? (
                <div className="text-center p-10">
                  <Mail className="mx-auto size-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">
                    Oops! Link Expired
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {data?.response_message ||
                      "A new link has been sent to your email. You may close this window."}
                  </p>
                </div>
              ) : (
                <div className="h-[87vh] flex flex-col items-center max-w-[100vw] p-3">
                  <div className="flex justify-between items-center py-3 w-full">
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={goToPrevPage}
                        disabled={pageNumber <= 1}
                        aria-label="Previous page"
                        variant="outline"
                        size="icon"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <p
                        className="text-muted-foreground text-sm"
                        aria-live="polite"
                      >
                        Page{" "}
                        <span className="text-foreground">{pageNumber}</span> of{" "}
                        <span className="text-foreground">{numPages}</span>
                      </p>
                      <Button
                        onClick={goToNextPage}
                        disabled={pageNumber >= numPages}
                        aria-label="Next page"
                        variant="outline"
                        size="icon"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="bg-white border border-border p-1 rounded-lg flex gap-2 items-center">
                      <button
                        onClick={zoomOut}
                        disabled={scale <= 0.2}
                        aria-label="Zoom out"
                        className="p-2 hover:bg-gray-50 rounded-md"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <ZoomIn className="h-5 w-5 text-gray-400" />
                      <button
                        onClick={zoomIn}
                        aria-label="Zoom in"
                        className="p-2 hover:bg-gray-50 rounded-md"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      overflowX: "auto",
                      maxWidth: "100vw",
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                  >
                    <canvas ref={canvasRef} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export function useInvoicePreview() {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");
  const linkQuery = useQuery<any, any>({
    queryKey: ["invoice", "link", token],
    queryFn: async () => {
      //@ts-ignore
      const response = await api.get(
        `/platform/invoices/diego_is_here/link/${token}`
      );

      const location =
        response.headers?.location || response.data?.location || null;
      return {
        ...response.data,
        location,
      };
    },
    enabled: Boolean(token),
  });
  const [htmlContent, setHtmlContent] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  useEffect(() => {
    const base64Invoice = linkQuery?.data?.data?.base64_html;
    if (base64Invoice) {
      try {
        const decodedHtml = atob(base64Invoice);
        const logoMatch = decodedHtml.match(
          /<img[^>]+src=["'](.*?)["'][^>]*class="logo"/i
        );
        if (logoMatch && logoMatch[1]) {
          setLogoUrl(logoMatch[1]);
        }

        setHtmlContent(decodedHtml);
      } catch (error) {
        console.error("Error decoding base64 invoice:", error);
      }
    }
  }, [linkQuery?.data?.data?.base64_html]);

  return {
    htmlContent,
    logoUrl,
    ...linkQuery,
  };
}

export function useCanvasPreview(htmlContent) {
  const [pages, setPages] = useState<HTMLCanvasElement[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const touchStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Generate page canvases from HTML content
  useEffect(() => {
    if (!htmlContent) {
      setPages([]);
      setNumPages(0);
      return;
    }

    const generatePages = async () => {
      try {
        // Create a hidden div for measuring content
        const hiddenDiv = document.createElement("div");
        hiddenDiv.style.position = "absolute";
        hiddenDiv.style.top = "-9999px"; // Move off-screen
        hiddenDiv.style.left = "-9999px";
        hiddenDiv.style.width = "794px"; // A4 width at 96 DPI
        hiddenDiv.style.visibility = "visible"; // Ensure content renders
        hiddenDiv.innerHTML = htmlContent;
        document.body.appendChild(hiddenDiv);

        // Wait for content to render
        await new Promise((resolve) => setTimeout(resolve, 500));

        const pageHeightPx = 1123; // A4 height at 96 DPI
        const contentHeight = hiddenDiv.scrollHeight;
        const totalPages = Math.ceil(contentHeight / pageHeightPx);

        const pageCanvases: HTMLCanvasElement[] = [];

        // Render each page
        for (let page = 0; page < totalPages; page++) {
          // Create a page-specific div
          const pageDiv = document.createElement("div");
          pageDiv.style.width = "794px";
          pageDiv.style.height = `${pageHeightPx}px`;
          pageDiv.style.overflow = "hidden";
          pageDiv.style.position = "absolute";
          pageDiv.style.top = "-9999px";
          pageDiv.style.left = "-9999px";

          // Clone the content
          pageDiv.innerHTML = htmlContent;

          document.body.appendChild(pageDiv);

          // Wait for page content to render
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Adjust content to show only the current page's portion
          const contentWrapper = document.createElement("div");
          contentWrapper.style.position = "relative";
          contentWrapper.style.top = `-${page * pageHeightPx}px`;
          while (pageDiv.firstChild) {
            contentWrapper.appendChild(pageDiv.firstChild);
          }
          pageDiv.appendChild(contentWrapper);

          const canvas = await html2canvas(pageDiv, {
            scale: 1,
            useCORS: true,
            width: 794,
            height: pageHeightPx,
            logging: false,
          });

          pageCanvases.push(canvas);

          // Clean up
          document.body.removeChild(pageDiv);
        }

        setPages(pageCanvases);
        setNumPages(totalPages);
        setPageNumber(1);

        // Clean up
        document.body.removeChild(hiddenDiv);
      } catch (error) {
        console.error("Error generating pages:", error);
      }
    };

    generatePages();
  }, [htmlContent]);

  // Render current page to viewer canvas
  const renderPage = useCallback(() => {
    if (
      !pages.length ||
      !canvasRef.current ||
      pageNumber < 1 ||
      pageNumber > numPages
    )
      return;

    const pageCanvas = pages[pageNumber - 1];
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    if (!context) return;

    const containerWidth = canvas.parentElement?.offsetWidth || 300;
    const scaleToFit = Math.min(containerWidth / pageCanvas.width, 1.5);
    const scaledWidth = pageCanvas.width * scale * scaleToFit;
    const scaledHeight = pageCanvas.height * scale * scaleToFit;

    const pixelRatio = Math.min(window.devicePixelRatio, 1.5);
    canvas.width = scaledWidth * pixelRatio;
    canvas.height = scaledHeight * pixelRatio;
    canvas.style.width = `${scaledWidth}px`;
    canvas.style.height = `${scaledHeight}px`;
    context.scale(pixelRatio, pixelRatio);

    context.drawImage(pageCanvas, 0, 0, scaledWidth, scaledHeight);
    console.log(`Rendered page ${pageNumber}`);
  }, [pages, pageNumber, scale, numPages]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  // Debounced resize handler
  useEffect(() => {
    let debounceTimer: any;
    const handleResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        renderPage();
      }, 100);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, [renderPage]);

  const goToPrevPage = () => pageNumber > 1 && setPageNumber(pageNumber - 1);
  const goToNextPage = () =>
    pageNumber < numPages && setPageNumber(pageNumber + 1);

  const zoomIn = () => setScale(scale + 0.2);
  const zoomOut = () => scale > 0.2 && setScale(scale - 0.2);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const touchEndX = e.changedTouches[0].clientX;
    const deltaX = touchEndX - touchStartRef.current.x;
    if (deltaX > 50 && pageNumber > 1) goToPrevPage();
    if (deltaX < -50 && pageNumber < numPages) goToNextPage();
  };

  return {
    canvasRef,
    touchStartRef,
    pages,
    numPages,
    pageNumber,
    scale,
    zoomIn,
    zoomOut,
    handleTouchStart,
    handleTouchEnd,
    goToNextPage,
    goToPrevPage,
  };
}
